"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { showToast } from "@/app/_module/utils/show-toast";

export type DialerMode = 'idle' | 'power' | 'multiline';
export type WidgetTab = 'call' | 'insurabot';

/**
 * Where the current set of selectedLeads originated from. Drives downstream
 * behavior — most importantly, 'dialiq' source enables Predictive Dial in
 * Power Dialer sessions for eligible agencies.
 */
export type DialerSource = 'manual' | 'dialiq';

interface UnifiedDialerState {
  // Widget visibility
  isWidgetOpen: boolean;
  isMinimized: boolean;
  activeTab: WidgetTab;

  // Lead selection (from any page)
  selectedLeads: Lead[];

  // Source of the current lead selection — drives Predictive Dial enablement.
  dialerSource: DialerSource;

  // Dialer session
  dialerMode: DialerMode;
  sessionActive: boolean;
  currentLeadIndex: number;
  resumeOffset: number; // Number of leads already completed from previous session (for resume)

  // Manual call lead status tracking (for session complete stats)
  manualCallLeadStatus: string | null;

  // InsuraHelp panel from dialer (to coordinate with InsuraBot)
  isInsuraHelpFromDialerOpen: boolean;

  // InsuraBot state (to coordinate positioning)
  isInsuraBotOpen: boolean;
  insuraBotRect: { right: number; bottom: number; width: number; height: number } | null;

  // InsuraHelp widget state (to coordinate with InsuraBot)
  isInsuraHelpWidgetOpen: boolean;

  // Bug Report widget state (to hide InsuraHelp button when open)
  isBugReportOpen: boolean;

  // Delete confirmation state (triggered from widget, handled by leads page)
  showDeleteConfirmation: boolean;

  // Bulk status change state (triggered from widget, handled by leads page)
  showBulkStatusChange: boolean;

  // Agency context for the active session (persisted to survive refresh)
  sessionAgencyId: string | null;
  sessionAgencyData: { id: string; name: string; role: string } | null;

  // Eavesdrop / coach state (agency owner listening / whispering / barging)
  eavesdropActive: boolean;
  eavesdropCallSid: string | null;
  eavesdropStreamSid: string | null;
  eavesdropMode: 'listen' | 'whisper' | 'barge';
  eavesdropAgentName: string;
  eavesdropLeadName: string;
  eavesdropStartedAt: Date | null;
  eavesdropFollowingAgentId: string | null;
  /** Push-to-talk state — true while manager is actively transmitting in
   *  whisper / barge. Source of truth lives in LiveCalls (it owns the
   *  Socket.IO connection + mic capture); LiveCalls writes here so the
   *  floating widget can render the right UI from anywhere in the app. */
  coachMicActive: boolean;
  /** RMS volume meter for outbound mic (0..1). Drives the level meter
   *  in both LiveCalls and the floating widget. */
  coachMicVolume: number;
  /** Smoothed RMS of inbound call audio (0..1). Drives the waveform
   *  visualizer in the widget so the bars react to who's talking. */
  coachInboundLevel: number;
}

interface UnifiedDialerContextType extends UnifiedDialerState {
  // Widget controls
  openWidget: () => void;
  closeWidget: () => void;
  minimizeWidget: () => void;
  maximizeWidget: () => void;
  setActiveTab: (tab: WidgetTab) => void;

  // Lead selection
  // Second arg `source` lets callers (DialIQ, RedialChips, etc.) mark how
  // these leads got into the queue. Drives Predictive Dial activation.
  // Defaults to 'manual' when omitted — keeps legacy callers working.
  addLeadsToDialer: (leads: Lead[], source?: DialerSource, opts?: { first?: boolean }) => void;
  setDialerLeads: (leads: Lead[]) => void; // Replace all leads (for syncing with table selection)
  removeLeadFromDialer: (leadId: string) => void;
  clearSelectedLeads: () => void;

  // Dialer session controls
  startPowerDialer: () => void;
  startMultiLineDialer: () => void;
  stopDialerSession: () => void;
  nextLead: () => void;
  skipLead: () => void;

  // Session state
  setSessionActive: (active: boolean) => void;
  setCurrentLeadIndex: (index: number) => void;
  setResumeOffset: (offset: number) => void;

  // Session recovery (page refresh prompt)
  pendingRecovery: boolean;
  confirmRecovery: () => void;
  declineRecovery: () => void;

  // InsuraHelp from dialer
  setInsuraHelpFromDialerOpen: (open: boolean) => void;

  // InsuraBot state
  setInsuraBotOpen: (open: boolean) => void;
  setInsuraBotRect: (rect: { right: number; bottom: number; width: number; height: number } | null) => void;

  // InsuraHelp widget state
  setInsuraHelpWidgetOpen: (open: boolean) => void;

  // Bug Report widget state
  setBugReportOpen: (open: boolean) => void;

  // Delete confirmation (triggered from widget, handled by MultiSelectActions)
  /** Returns false (and does nothing) when no page is hosting the confirmation
   *  dialog — the caller should tell the user to open the Leads page. */
  requestDeleteSelectedLeads: () => boolean;
  setShowDeleteConfirmation: (show: boolean) => void;
  /** Hosts of the dialog (Leads pages) register while mounted; returns the unregister fn. */
  registerDeleteConfirmationHost: () => () => void;

  // Bulk status change (triggered from widget, handled by MultiSelectActions)
  requestBulkStatusChange: () => void;
  setShowBulkStatusChange: (show: boolean) => void;

  // Manual call lead status tracking (for session complete stats)
  trackManualCallLeadStatus: (status: string) => void;
  clearManualCallLeadStatus: () => void;

  // Agency context for the active session
  sessionAgencyId: string | null;
  sessionAgencyData: { id: string; name: string; role: string } | null;

  // Eavesdrop controls
  /** `autoOpenWidget` defaults true; the Live Calls console passes false (it owns its own monitor rail). */
  startEavesdrop: (callSid: string, streamSid: string, agentName: string, leadName: string, mode?: 'listen' | 'whisper' | 'barge', autoOpenWidget?: boolean) => void;
  stopEavesdrop: () => void;
  setEavesdropFollowAgent: (agentId: string | null) => void;
  updateEavesdropLead: (leadName: string) => void;
  setEavesdropMode: (mode: 'listen' | 'whisper' | 'barge') => void;
  // Coach mic state setters used by LiveCalls (the socket owner) to
  // sync state into context. Widget reads `coachMicActive`/`coachMicVolume`
  // and dispatches `requestCoachMicStart`/`requestCoachMicStop` to
  // ask LiveCalls to push-to-talk on its behalf.
  setCoachMicActive: (active: boolean) => void;
  setCoachMicVolume: (volume: number) => void;
  setCoachInboundLevel: (level: number) => void;
  requestCoachMicStart: () => void;
  requestCoachMicStop: () => void;
  eavesdropActive: boolean;
  eavesdropCallSid: string | null;
  eavesdropStreamSid: string | null;
  eavesdropMode: 'listen' | 'whisper' | 'barge';
  eavesdropAgentName: string;
  eavesdropLeadName: string;
  eavesdropStartedAt: Date | null;
  eavesdropFollowingAgentId: string | null;
  coachMicActive: boolean;
  coachMicVolume: number;
  coachInboundLevel: number;
}

const UnifiedDialerContext = createContext<UnifiedDialerContextType | undefined>(undefined);

const STORAGE_KEY = 'unified-dialer-leads';
const SESSION_STORAGE_KEY = 'unified-dialer-session';

interface UnifiedDialerProviderProps {
  children: ReactNode;
}

export function UnifiedDialerProvider({ children }: UnifiedDialerProviderProps) {
  const [isWidgetOpen, setIsWidgetOpen] = useState(true); // Always show widget
  const [isMinimized, setIsMinimized] = useState(true); // Start minimized as "Dialer" square
  const [activeTab, setActiveTab] = useState<WidgetTab>('call');
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const selectedLeadsRef = useRef<Lead[]>([]);
  // Keep ref in sync with state so callbacks always have latest value
  useEffect(() => { selectedLeadsRef.current = selectedLeads; }, [selectedLeads]);
  // Tracks where the current selectedLeads originated from. Reset to 'manual'
  // on clear; set explicitly by callers like DialIQPanel via addLeadsToDialer.
  const [dialerSource, setDialerSource] = useState<DialerSource>('manual');
  const [dialerMode, setDialerMode] = useState<DialerMode>('idle');
  const [sessionActive, setSessionActive] = useState(false);
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0);
  const [resumeOffset, setResumeOffset] = useState(0);
  const [isInsuraHelpFromDialerOpen, setIsInsuraHelpFromDialerOpen] = useState(false);
  const [isInsuraBotOpen, setIsInsuraBotOpen] = useState(false);
  const [insuraBotRect, setInsuraBotRect] = useState<{ right: number; bottom: number; width: number; height: number } | null>(null);
  const [isInsuraHelpWidgetOpen, setIsInsuraHelpWidgetOpen] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showBulkStatusChange, setShowBulkStatusChange] = useState(false);
  const [manualCallLeadStatus, setManualCallLeadStatus] = useState<string | null>(null);
  const [sessionAgencyId, setSessionAgencyId] = useState<string | null>(null);
  const [sessionAgencyData, setSessionAgencyData] = useState<{ id: string; name: string; role: string } | null>(null);

  // Eavesdrop state
  const [eavesdropActive, setEavesdropActive] = useState(false);
  const [eavesdropCallSid, setEavesdropCallSid] = useState<string | null>(null);
  const [eavesdropStreamSid, setEavesdropStreamSid] = useState<string | null>(null);
  const [eavesdropAgentName, setEavesdropAgentName] = useState("");
  const [eavesdropLeadName, setEavesdropLeadName] = useState("");
  const [eavesdropStartedAt, setEavesdropStartedAt] = useState<Date | null>(null);
  const [eavesdropFollowingAgentId, setEavesdropFollowingAgentId] = useState<string | null>(null);
  const [eavesdropMode, setEavesdropModeState] = useState<'listen' | 'whisper' | 'barge'>('listen');
  const [coachMicActive, setCoachMicActive] = useState(false);
  const [coachMicVolume, setCoachMicVolume] = useState(0);
  // Inbound audio level (0..1, smoothed RMS) from the call we're listening to.
  // Drives the waveform visualizer in the widget so the bars actually react
  // to who's talking instead of looking like static random bars.
  const [coachInboundLevel, setCoachInboundLevel] = useState(0);

  // Widget calls these to ask LiveCalls (which owns the Socket.IO + mic)
  // to start/stop transmission. Window events are the cheapest cross-tree
  // bus for this — same pattern we already use for emergency-pause.
  const requestCoachMicStart = useCallback(() => {
    try { window.dispatchEvent(new Event('coach:request-mic-start')); } catch {}
  }, []);
  const requestCoachMicStop = useCallback(() => {
    try { window.dispatchEvent(new Event('coach:request-mic-stop')); } catch {}
  }, []);

  /**
   * Register a coach/eavesdrop session in global state.
   *
   * `autoOpenWidget` (default true) controls whether the floating widget is
   * force-maximized. The Live Calls console passes FALSE: it has its own
   * sticky monitor rail, so popping the widget open there showed the plain
   * dialer UI over a page that already has every control — the widget's
   * eavesdrop view is suppressed on that route, so un-minimizing it just
   * surfaced an unrelated panel ("opens the dialer widget for no reason").
   *
   * State is still recorded either way, which is what lets the widget take
   * over seamlessly the moment the supervisor navigates off the console.
   */
  const startEavesdrop = useCallback((
    callSid: string,
    streamSid: string,
    agentName: string,
    leadName: string,
    mode: 'listen' | 'whisper' | 'barge' = 'listen',
    autoOpenWidget: boolean = true,
  ) => {
    setEavesdropActive(true);
    setEavesdropCallSid(callSid);
    setEavesdropStreamSid(streamSid);
    setEavesdropAgentName(agentName);
    setEavesdropLeadName(leadName);
    setEavesdropStartedAt(new Date());
    setEavesdropModeState(mode);
    if (autoOpenWidget) {
      // Open and maximize the widget to show eavesdrop UI
      setIsMinimized(false);
      setActiveTab('call');
    }
  }, []);

  const setEavesdropMode = useCallback((mode: 'listen' | 'whisper' | 'barge') => {
    setEavesdropModeState(mode);
  }, []);

  const stopEavesdrop = useCallback(() => {
    setEavesdropActive(false);
    setEavesdropCallSid(null);
    setEavesdropStreamSid(null);
    setEavesdropAgentName("");
    setEavesdropLeadName("");
    setEavesdropStartedAt(null);
    setEavesdropFollowingAgentId(null);
    setEavesdropModeState('listen');
    setCoachMicActive(false);
    setCoachMicVolume(0);
    setIsMinimized(true);
  }, []);

  const setEavesdropFollowAgent = useCallback((agentId: string | null) => {
    setEavesdropFollowingAgentId(agentId);
  }, []);

  const updateEavesdropLead = useCallback((leadName: string) => {
    setEavesdropLeadName(leadName);
    setEavesdropStartedAt(new Date()); // Reset timer for new call
  }, []);

  // Session recovery state — prevents auto-dialing on page refresh
  const [pendingRecovery, setPendingRecovery] = useState(false);
  const [pendingRecoveryData, setPendingRecoveryData] = useState<{
    dialerMode: DialerMode;
    currentLeadIndex: number;
    sessionAgencyId: string | null;
    sessionAgencyData: { id: string; name: string; role: string } | null;
  } | null>(null);

  // Load persisted leads AND session state from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Restore leads
      const storedLeads = localStorage.getItem(STORAGE_KEY);
      if (storedLeads) {
        try {
          const leads = JSON.parse(storedLeads);
          if (Array.isArray(leads) && leads.length > 0) {
            setSelectedLeads(leads);
            setIsWidgetOpen(true);
          }
        } catch (e) {
          // Invalid data, ignore
        }
      }

      // Check for recoverable session state — DON'T auto-resume, prompt instead
      // SKIP recovery if a manual call auto-stop is in progress (agent clicked Call on lead detail)
      const manualCallPending = (() => { try { return sessionStorage.getItem('__multiline_manual_pause') === '1'; } catch { return false; } })();
      if (manualCallPending) {
        // Clear session persistence so multi-line doesn't auto-recover while making manual call
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }
      const storedSession = localStorage.getItem(SESSION_STORAGE_KEY);
      if (storedSession && !manualCallPending) {
        try {
          const session = JSON.parse(storedSession);
          if (session.sessionActive && session.dialerMode) {
            // Store recovery data but DON'T activate the session yet
            setPendingRecoveryData({
              dialerMode: session.dialerMode,
              currentLeadIndex: session.currentLeadIndex || 0,
              sessionAgencyId: session.sessionAgencyId || null,
              sessionAgencyData: session.sessionAgencyData || null,
            });
            setPendingRecovery(true);
            setIsMinimized(false); // Expand widget to show recovery prompt
          }
        } catch (e) {
          // Invalid data, ignore
        }
      }
    }
  }, []);

  // Persist leads to localStorage when they change.
  // Large bulk selections (thousands of full lead objects) can blow past the
  // browser's ~5MB localStorage quota and throw QuotaExceededError, which would
  // crash the provider. Degrade gracefully: try full objects, then a slim
  // projection (only what the queue/dialer needs to rehydrate), then a capped
  // slim list, and finally give up persisting rather than crash.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedLeads.length === 0) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      return;
    }
    const trySet = (payload: string) => {
      try {
        localStorage.setItem(STORAGE_KEY, payload);
        return true;
      } catch {
        return false;
      }
    };
    // 1) Full objects — fine for typical session-sized selections.
    if (trySet(JSON.stringify(selectedLeads))) return;
    // 2) Too big — store only the fields the queue/dialer needs to rehydrate.
    const slim = selectedLeads.map((l) => ({
      id: l.id,
      name: l.name,
      firstName: l.firstName,
      lastName: l.lastName,
      phone: l.phone,
      email: l.email,
      leadStat: l.leadStat,
      status: l.status,
      state: l.state,
      assignedToUserId: l.assignedToUserId,
    }));
    if (trySet(JSON.stringify(slim))) return;
    // 3) Still too big — cap to the dial-session size.
    if (trySet(JSON.stringify(slim.slice(0, MAX_SESSION_LEADS)))) return;
    // 4) Give up persisting rather than crash the provider.
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, [selectedLeads]);

  // Persist session state to localStorage when it changes (survives page refresh)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (sessionActive && dialerMode !== 'idle') {
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
          sessionActive,
          dialerMode,
          currentLeadIndex,
          sessionAgencyId,
          sessionAgencyData,
        }));
      } else {
        // Clear session storage when session ends
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
  }, [sessionActive, dialerMode, currentLeadIndex, sessionAgencyId, sessionAgencyData]);

  // Widget controls
  const openWidget = useCallback(() => {
    setIsWidgetOpen(true);
    setIsMinimized(false);
  }, []);

  const closeWidget = useCallback(() => {
    // Only close if no active session
    if (!sessionActive) {
      setIsWidgetOpen(false);
      setIsMinimized(false);
    }
  }, [sessionActive]);

  const minimizeWidget = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeWidget = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Lead selection
  // MAX_SESSION_LEADS — how many leads a single DIAL session may dial.
  // MAX_SELECTED_LEADS — how many may be SELECTED (for the count + bulk actions
  // like Move to Pipeline / Set Source, which are not dial-limited). Selecting
  // more than the dial cap is allowed; the dialer buttons lock at >500 so you
  // can't dial them, but bulk status/source changes still apply to all selected.
  const MAX_SESSION_LEADS = 500;
  const MAX_SELECTED_LEADS = 5000;

  const addLeadsToDialer = useCallback((leads: Lead[], source: DialerSource = 'manual', opts?: { first?: boolean }) => {
    setSelectedLeads(prev => {
      let merged: Lead[];
      if (opts?.first) {
        // These lead the queue in their own order (a resumed power session's position counts
        // from its first lead); anything already queued follows.
        const ids = new Set(leads.map(l => l.id));
        merged = [...leads, ...prev.filter(l => !ids.has(l.id))];
      } else {
        const existingIds = new Set(prev.map(l => l.id));
        merged = [...prev, ...leads.filter(l => !existingIds.has(l.id))];
      }
      const capped = merged.slice(0, MAX_SESSION_LEADS);
      selectedLeadsRef.current = capped;
      return capped;
    });
    // If a NON-manual source is loading leads while we have a manual queue,
    // promote to that source (DialIQ wins over manual). If we're already on a
    // non-manual source and the new caller is manual, keep the existing source.
    setDialerSource(prev => (source !== 'manual' ? source : prev));
    // Mirror MultiSelectActions' open+maximize behavior. Without setting
    // isWidgetOpen the widget stays hidden when callers (DialIQ, RedialChips)
    // load leads from a closed state — leads are queued but the user sees
    // nothing happen, which looked like the dialer was broken.
    setIsWidgetOpen(true);
    setIsMinimized(false);
  }, []);

  // Replace all leads in dialer (for syncing with table selection)
  // IMPORTANT: Merge new leads with existing queue instead of replacing
  // This prevents filter/search changes from clearing the dialer queue
  const setDialerLeads = useCallback((leads: Lead[]) => {
    // If trying to set empty leads but we already have leads queued AND session is active,
    // ignore the call - this prevents losing queued leads when user searches/filters
    // during an active session. BUT if session is NOT active, allow clearing.
    if (leads.length === 0 && selectedLeads.length > 0 && sessionActive) {
      return; // Protect queued leads from being cleared by page state resets during active session
    }

    // If session is active and we have existing leads, merge new selections instead of replacing.
    // During an active DIAL session the queue stays capped at the dial limit.
    if (selectedLeads.length > 0 && leads.length > 0 && sessionActive) {
      setSelectedLeads(prev => {
        const existingIds = new Set(prev.map(l => l.id));
        const newLeads = leads.filter(l => !existingIds.has(l.id));
        const merged = [...prev, ...newLeads];
        return merged.slice(0, MAX_SESSION_LEADS);
      });
    } else {
      // Plain table-selection sync (not dialing yet): allow the full selection
      // up to MAX_SELECTED_LEADS so the count is accurate and bulk actions can
      // operate on all of them. The dial buttons lock at >500 separately.
      setSelectedLeads(leads.slice(0, MAX_SELECTED_LEADS));
    }

    // Auto-expand widget when leads are set (if any)
    if (leads.length > 0) {
      setIsMinimized(false);
    }
  }, [selectedLeads.length, sessionActive]);

  const removeLeadFromDialer = useCallback((leadId: string) => {
    setSelectedLeads(prev => prev.filter(l => l.id !== leadId));
  }, []);

  const clearSelectedLeads = useCallback(() => {
    setSelectedLeads([]);
    selectedLeadsRef.current = [];
    setDialerSource('manual');
    setDialerMode('idle');
    setSessionActive(false);
    setCurrentLeadIndex(0);
    setResumeOffset(0);
    setSessionAgencyId(null);
    setSessionAgencyData(null);
    // Clear persisted session state
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      // Dispatch event so the leads table can clear its selection
      window.dispatchEvent(new CustomEvent('dialer-leads-cleared'));
    }
  }, []);

  // Helper to capture current agency context for session persistence
  const captureAgencyContext = useCallback(() => {
    const currentAgency = AgencyTokenManager.getCurrentAgency();
    if (currentAgency) {
      setSessionAgencyId(currentAgency.id);
      setSessionAgencyData({
        id: currentAgency.id,
        name: currentAgency.name,
        role: (currentAgency as any).role || 'OWNER',
      });
    }
  }, []);

  // Dialer session controls
  const startPowerDialer = useCallback(() => {
    if (selectedLeadsRef.current.length === 0) return;
    // Dialing is hard-capped at 500. The widget buttons also lock at >500, but
    // guard here too so no caller can start a dial session over the limit.
    if (selectedLeadsRef.current.length > MAX_SESSION_LEADS) return;
    captureAgencyContext();
    setDialerMode('power');
    setSessionActive(true);
    setCurrentLeadIndex(0);
    setResumeOffset(0); // Reset for fresh sessions
  }, [captureAgencyContext]);

  const startMultiLineDialer = useCallback(() => {
    if (selectedLeadsRef.current.length === 0) return;
    if (selectedLeadsRef.current.length > MAX_SESSION_LEADS) return; // dialing capped at 500
    captureAgencyContext();
    setDialerMode('multiline');
    setSessionActive(true);
    setCurrentLeadIndex(0);
    setResumeOffset(0); // Reset for fresh sessions
  }, [captureAgencyContext]);

  const stopDialerSession = useCallback(() => {
    setDialerMode('idle');
    setSessionActive(false);
    setCurrentLeadIndex(0);
    setSessionAgencyId(null);
    setSessionAgencyData(null);
    // Clear persisted session state
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, []);

  const nextLead = useCallback(() => {
    if (currentLeadIndex < selectedLeads.length - 1) {
      setCurrentLeadIndex(prev => prev + 1);
    } else {
      // Session complete
      stopDialerSession();
      clearSelectedLeads();
    }
  }, [currentLeadIndex, selectedLeads.length, stopDialerSession, clearSelectedLeads]);

  const skipLead = useCallback(() => {
    nextLead();
  }, [nextLead]);

  // Request to delete selected leads (triggers confirmation dialog in MultiSelectActions).
  //
  // ⚠️ The "Delete selected leads" button lives in the widget, which is mounted on
  // EVERY page, but the dialog is only hosted by the Leads pages. Before 2026-09-07
  // a click anywhere else set this flag with nothing visible, and it stayed armed
  // until a Leads page mounted the host — at which point a "Delete 194 leads?"
  // dialog opened by itself, mid power-dial session (MEI Financial, Eljesa Shala).
  // Now: no host mounted → refuse (caller toasts); host unmount → flag cleared.
  const deleteHostsRef = useRef(0);
  const registerDeleteConfirmationHost = useCallback(() => {
    deleteHostsRef.current += 1;
    return () => {
      deleteHostsRef.current = Math.max(0, deleteHostsRef.current - 1);
      if (deleteHostsRef.current === 0) setShowDeleteConfirmation(false);
    };
  }, []);
  const requestDeleteSelectedLeads = useCallback((): boolean => {
    if (deleteHostsRef.current === 0) return false;
    setShowDeleteConfirmation(true);
    return true;
  }, []);

  // Request to bulk change status (triggers pipeline picker in MultiSelectActions)
  const requestBulkStatusChange = useCallback(() => {
    setShowBulkStatusChange(true);
  }, []);

  // Track lead status during manual calls (for session complete stats)
  const trackManualCallLeadStatus = useCallback((status: string) => {
    setManualCallLeadStatus(status);
  }, []);

  const clearManualCallLeadStatus = useCallback(() => {
    setManualCallLeadStatus(null);
  }, []);

  // Session recovery callbacks
  const confirmRecovery = useCallback(() => {
    if (!pendingRecoveryData) return;
    // Apply the stored session data — this activates the dialer
    setDialerMode(pendingRecoveryData.dialerMode);
    setSessionActive(true);
    setCurrentLeadIndex(pendingRecoveryData.currentLeadIndex);
    if (pendingRecoveryData.sessionAgencyId) {
      setSessionAgencyId(pendingRecoveryData.sessionAgencyId);
      setSessionAgencyData(pendingRecoveryData.sessionAgencyData);
      // Restore agency token context
      const currentAgencyId = AgencyTokenManager.getCurrentAgencyId();
      if (currentAgencyId !== pendingRecoveryData.sessionAgencyId && pendingRecoveryData.sessionAgencyData) {
        AgencyTokenManager.setCurrentAgency({
          id: pendingRecoveryData.sessionAgencyData.id,
          name: pendingRecoveryData.sessionAgencyData.name,
          description: '',
          contactEmail: '',
          isActive: true,
          role: pendingRecoveryData.sessionAgencyData.role,
          permissions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: null,
        });
      }
    }
    setPendingRecovery(false);
    setPendingRecoveryData(null);
  }, [pendingRecoveryData]);

  const declineRecovery = useCallback(() => {
    // Clear all persisted dialer state
    setPendingRecovery(false);
    setPendingRecoveryData(null);
    setSelectedLeads([]);
    setDialerMode('idle');
    setSessionActive(false);
    setCurrentLeadIndex(0);
    setSessionAgencyId(null);
    setSessionAgencyData(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('multiline-dialer-session-stats');
      localStorage.removeItem('power-dialer-call-results');
      localStorage.removeItem('active-call-session');
      window.dispatchEvent(new CustomEvent('dialer-leads-cleared'));
    }
    setIsMinimized(true);
  }, []);

  const value: UnifiedDialerContextType = {
    // State
    isWidgetOpen,
    isMinimized,
    activeTab,
    selectedLeads,
    dialerSource,
    dialerMode,
    sessionActive,
    currentLeadIndex,
    resumeOffset,
    isInsuraHelpFromDialerOpen,
    isInsuraBotOpen,
    insuraBotRect,
    isInsuraHelpWidgetOpen,
    isBugReportOpen,
    showDeleteConfirmation,
    showBulkStatusChange,
    manualCallLeadStatus,
    sessionAgencyId,
    sessionAgencyData,
    // Widget controls
    openWidget,
    closeWidget,
    minimizeWidget,
    maximizeWidget,
    setActiveTab,
    // Lead selection
    addLeadsToDialer,
    setDialerLeads,
    removeLeadFromDialer,
    clearSelectedLeads,
    // Dialer session controls
    startPowerDialer,
    startMultiLineDialer,
    stopDialerSession,
    nextLead,
    skipLead,
    // Session state setters — wrap to capture agency context on activation
    setSessionActive: useCallback((active: boolean) => {
      if (active && !sessionActive) {
        captureAgencyContext();
      }
      setSessionActive(active);
    }, [sessionActive, captureAgencyContext]),
    setCurrentLeadIndex,
    setResumeOffset,
    // Session recovery
    pendingRecovery,
    confirmRecovery,
    declineRecovery,
    // InsuraHelp from dialer
    setInsuraHelpFromDialerOpen: setIsInsuraHelpFromDialerOpen,
    // InsuraBot state
    setInsuraBotOpen: setIsInsuraBotOpen,
    setInsuraBotRect,
    // InsuraHelp widget state
    setInsuraHelpWidgetOpen: setIsInsuraHelpWidgetOpen,
    // Bug Report widget state
    setBugReportOpen: setIsBugReportOpen,
    // Delete confirmation
    requestDeleteSelectedLeads,
    setShowDeleteConfirmation,
    registerDeleteConfirmationHost,
    // Bulk status change
    requestBulkStatusChange,
    setShowBulkStatusChange,
    // Manual call lead status tracking
    trackManualCallLeadStatus,
    clearManualCallLeadStatus,
    // Eavesdrop
    startEavesdrop,
    stopEavesdrop,
    setEavesdropFollowAgent,
    updateEavesdropLead,
    setEavesdropMode,
    setCoachMicActive,
    setCoachMicVolume,
    coachInboundLevel,
    setCoachInboundLevel,
    requestCoachMicStart,
    requestCoachMicStop,
    eavesdropActive,
    eavesdropCallSid,
    eavesdropStreamSid,
    eavesdropMode,
    eavesdropAgentName,
    eavesdropLeadName,
    eavesdropStartedAt,
    eavesdropFollowingAgentId,
    coachMicActive,
    coachMicVolume,
  };

  return (
    <UnifiedDialerContext.Provider value={value}>
      {children}
    </UnifiedDialerContext.Provider>
  );
}

export function useUnifiedDialer() {
  const context = useContext(UnifiedDialerContext);
  if (!context) {
    throw new Error("useUnifiedDialer must be used within a UnifiedDialerProvider");
  }
  return context;
}
